// Small shared pieces the three counter screens use.
//
// Pulled out for the same reason features/ip-case-sheet/ui.tsx exists: the
// same money formatter, the same "nothing here yet" block and the same
// quantity stepper appearing in three files is three implementations that
// drift. Everything here is presentation only — no data fetching, no rules.
import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { PaymentMode } from '../types';

// ── Money ────────────────────────────────────────────────────────
//
// en-IN grouping (1,23,456 — lakhs, not thousands) because this is what a
// bill handed to an Indian patient looks like, and a pharmacist reconciling a
// till reads the grouping before the digits.

const INR = new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const INR_WHOLE = new Intl.NumberFormat('en-IN', {
  style: 'currency', currency: 'INR', maximumFractionDigits: 0,
});

/** Exact rupees — anything a customer is asked to pay or is refunded. */
export const money = (v: string | number | null | undefined) => INR.format(parseFloat(String(v ?? 0)) || 0);

/** Rounded — headline figures on a stat tile, where paise are noise. */
export const moneyWhole = (v: string | number | null | undefined) => INR_WHOLE.format(parseFloat(String(v ?? 0)) || 0);

export const shortDate = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

export const shortDateTime = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export const timeOnly = (v: string | null | undefined) =>
  v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';

// ── Layout atoms ─────────────────────────────────────────────────

/**
 * An in-card empty state. Dashed rather than solid so it reads as "a slot
 * waiting to be filled" instead of "a card with nothing in it" — the same
 * treatment the Case Sheet's own sections use. Distinct from
 * components/ui/States' EmptyState, which is a whole-panel card in its own
 * right and would nest a card inside a card here.
 */
export function Empty({ label, hint, className }: { label: string; hint?: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-lg border border-dashed border-border px-4 py-6 text-center', className)}>
      <div className="text-[13px] text-ink-muted">{label}</div>
      {hint && <div className="mt-1 text-[11.5px] text-ink-muted/75">{hint}</div>}
    </div>
  );
}

/** A small uppercase caption over a value — the Case Sheet's Field, same specs. */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">{label}</div>
      <div className="mt-0.5 break-words text-[13px]">{children ?? '—'}</div>
    </div>
  );
}

/**
 * One line of a totals block. `tone` carries meaning, not decoration:
 * 'credit' is money coming off the bill (a discount, a redemption) and is the
 * only thing rendered with a minus sign, so the arithmetic reads down the
 * column without the operator having to work out which lines subtract.
 */
export function TotalLine({
  label, value, tone = 'normal', hint,
}: {
  label: ReactNode;
  value: number;
  tone?: 'normal' | 'credit' | 'grand';
  hint?: ReactNode;
}) {
  if (tone === 'grand') {
    return (
      <div className="mt-2 flex items-baseline justify-between border-t border-line pt-2.5">
        <div className="text-[13px] font-semibold">{label}</div>
        <div className="text-xl font-bold tracking-tight">{money(value)}</div>
      </div>
    );
  }
  return (
    <div className="flex items-baseline justify-between py-[3px] text-[13px]">
      <div className="text-ink-muted">
        {label}
        {hint && <span className="ml-1 text-[11px] text-ink-muted/70">{hint}</span>}
      </div>
      <div className={cn('tabular-nums', tone === 'credit' && 'text-success-ink')}>
        {tone === 'credit' ? `− ${money(Math.abs(value))}` : money(value)}
      </div>
    </div>
  );
}

// ── Step rail ────────────────────────────────────────────────────

export interface Step {
  id: string;
  label: string;
  /** done = behind us, active = where the operator is now, todo = ahead. */
  state: 'done' | 'active' | 'todo';
}

/**
 * The five steps at the counter, always visible.
 *
 * A till operator is interrupted constantly — a phone call, a second customer,
 * a question about a strip. Without a persistent "you are here" the usual
 * recovery is to scroll and guess, and the failure mode is saving a sale with
 * the prescription step silently skipped. Steps are indicators, NOT navigation:
 * clicking one would imply you can jump ahead of a customer who has not been
 * chosen yet.
 */
export function StepRail({ steps, className }: { steps: Step[]; className?: string }) {
  return (
    // Scrolls rather than wraps on a narrow till screen: a rail that reflows to
    // two lines stops reading as a sequence.
    <div className={cn('-mx-1 flex items-center gap-1 overflow-x-auto px-1 pb-1', className)}>
      {steps.map((s, i) => (
        <div key={s.id} className="flex shrink-0 items-center gap-1">
          <div
            className={cn(
              'flex items-center gap-1.5 rounded-full py-1 pl-1 pr-2.5 transition-colors',
              s.state === 'active' && 'bg-accent',
              s.state === 'done' && 'bg-transparent',
              s.state === 'todo' && 'bg-transparent'
            )}
          >
            <span
              className={cn(
                'grid h-[18px] w-[18px] place-content-center rounded-full text-[10px] font-bold',
                s.state === 'done' && 'bg-teal text-white',
                s.state === 'active' && 'bg-teal text-white',
                s.state === 'todo' && 'border border-border bg-card text-ink-muted'
              )}
            >
              {s.state === 'done' ? <Check size={11} strokeWidth={3} /> : i + 1}
            </span>
            <span
              className={cn(
                'whitespace-nowrap text-[11.5px]',
                s.state === 'active' ? 'font-semibold text-teal' : 'text-ink-muted'
              )}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && <span className="h-px w-3 shrink-0 bg-border" aria-hidden />}
        </div>
      ))}
    </div>
  );
}

// ── Quantity stepper ─────────────────────────────────────────────

/**
 * − / value / + for a cart line.
 *
 * Typing into a bare number input is the wrong default at a counter: the
 * common edit is "one more" or "one less", the operator is often not looking
 * at the screen, and a fat-fingered keystroke on a plain input silently sets
 * the quantity to 55 instead of 5. The field stays typable for a genuinely
 * large quantity; the buttons cover the common case and clamp to stock.
 */
export function QtyStepper({
  value, min = 1, max, onChange, disabled,
}: {
  value: number;
  min?: number;
  max: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  const btn =
    'grid h-7 w-7 place-content-center rounded-md border border-border text-ink-muted transition-colors ' +
    'hover:bg-muted disabled:pointer-events-none disabled:opacity-40';
  return (
    <div className="inline-flex items-center gap-1">
      <button type="button" className={btn} disabled={disabled || value <= min} onClick={() => onChange(clamp(value - 1))} aria-label="One fewer">
        −
      </button>
      <input
        type="number"
        className="h-7 w-12 rounded-md border border-border bg-card text-center text-[13px] tabular-nums outline-none focus:border-teal"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => onChange(clamp(parseInt(e.target.value, 10) || min))}
        aria-label="Quantity"
      />
      <button type="button" className={btn} disabled={disabled || value >= max} onClick={() => onChange(clamp(value + 1))} aria-label="One more">
        +
      </button>
    </div>
  );
}

// ── Payment mode / status ────────────────────────────────────────
//
// A refund is a payment. It moves the same four ways and it is either done or
// it is not — exactly like a sale. So the counter and the returns screen share
// these two controls rather than each growing its own, which is what keeps
// 'Pending' meaning one thing in this system instead of two.

export const PAYMENT_MODES: Array<{ id: PaymentMode; label: string }> = [
  { id: 'Cash', label: 'Cash' },
  { id: 'UPI', label: 'UPI' },
  { id: 'Card', label: 'Card' },
];

/**
 * Four buttons, not a dropdown. At a till the mode is chosen on almost every
 * transaction and is nearly always visible at a glance from across the counter;
 * a select hides the current value behind a click and costs two interactions
 * for something done hundreds of times a day.
 */
export function PaymentModePicker({
  value, onChange, label = 'Payment mode', disabled, modes,
}: {
  value: PaymentMode;
  onChange: (m: PaymentMode) => void;
  label?: string;
  disabled?: boolean;
  /** Restrict to a subset (e.g. Borrow & Return's hospital policy). Defaults to all three. */
  modes?: PaymentMode[];
}) {
  const shown = modes ? PAYMENT_MODES.filter((m) => modes.includes(m.id)) : PAYMENT_MODES;
  return (
    <div>
      <div className="lbl">{label}</div>
      <div className="grid grid-cols-3 gap-1.5">
        {shown.map((m) => (
          <button
            key={m.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(m.id)}
            className={cn(
              'rounded-lg border px-2 py-2 text-[12px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
              'disabled:pointer-events-none disabled:opacity-50',
              value === m.id ? 'border-teal bg-accent text-teal' : 'border-border text-ink-muted hover:bg-muted'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Paid vs Pending, with the consequence spelled out under each.
 *
 * On a refund this is the difference between money that has left the drawer and
 * money the hospital still owes, so the label alone is not enough — the caller
 * supplies the two sentences because they differ between taking money in and
 * paying it back out.
 */
export function PaymentStatusPicker({
  value, onChange, label = 'Payment status', paidHint, pendingHint,
}: {
  value: 'Paid' | 'Pending';
  onChange: (s: 'Paid' | 'Pending') => void;
  label?: string;
  paidHint: string;
  pendingHint: string;
}) {
  const options: Array<{ id: 'Paid' | 'Pending'; title: string; hint: string }> = [
    { id: 'Paid', title: 'Paid now', hint: paidHint },
    { id: 'Pending', title: 'Pending', hint: pendingHint },
  ];
  return (
    <div>
      <div className="lbl">{label}</div>
      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            className={cn(
              'rounded-lg border px-3 py-2 text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25',
              value === o.id ? 'border-teal bg-accent' : 'border-border hover:bg-muted'
            )}
          >
            <span className={cn('block text-[12.5px] font-semibold', value === o.id && 'text-teal')}>{o.title}</span>
            <span className="mt-0.5 block text-[10.5px] leading-snug text-ink-muted">{o.hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
